import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';

const INITIAL_FORM_DATA = {
  fullName: '',
  age: '',
  gender: '',
  address: '',
  landmark: '',
  pincode: '',
  emergencyName: '',
  emergencyPhone: '',
  allergies: '',
  medicalHistory: '',
  currentMedications: '',
  mobileNumber: '',
  emailId: '',
  password: '',
  reenterPassword: '',
};

function PatientRegister() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [locationName, setLocationName] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addressCount = formData.address.length;
  const passwordsMatch =
    formData.password === formData.reenterPassword && formData.reenterPassword !== '';

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password' || name === 'reenterPassword') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      setPincodeError('');
      setLocationName('');
      setIdDistrict(null);
      return;
    }

    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      setMobileError('');
      return;
    }

    if (name === 'emergencyPhone') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      return;
    }

    if (name === 'age') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 3);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      return;
    }

    if (name === 'emailId') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setEmailError('');
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setLocationName('');
    setIdDistrict(null);

    if (formData.pincode.length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(
        `/api/districts/validate-pincode?pincode=${formData.pincode}`,
      );
      const result = response.data?.data;

      if (result?.districtName) {
        setLocationName(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      return { ok: false, message };
    } catch {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  const validateContact = async (field = 'both') => {
    let message = '';
    if (field !== 'email') {
      setMobileError('');
    }
    if (field !== 'mobile') {
      setEmailError('');
    }

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(formData.emailId);
    const mobileValid =
      field === 'email' ? true : /^[6-9]\d{9}$/.test(formData.mobileNumber);

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        setEmailError('Enter a valid email.');
        message = 'Enter a valid email.';
      }
      if (!mobileValid) {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = message || 'Enter a valid 10-digit mobile number.';
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : formData.emailId || null,
        mobileNumber: field === 'email' ? null : formData.mobileNumber || null,
      };
      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') {
        setEmailError(message);
      } else if (field === 'mobile') {
        setMobileError(message);
      }
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  const validateForm = () => {
    if (formData.fullName.trim().length < 3) {
      toast.error('Please enter your full name (min 3 characters).');
      return false;
    }
    const age = parseInt(formData.age, 10);
    if (!age || age < 1 || age > 120) {
      toast.error('Please enter a valid age (1–120).');
      return false;
    }
    if (!formData.gender) {
      toast.error('Please select your gender.');
      return false;
    }
    if (formData.address.trim().length < 5) {
      toast.error('Please enter your address.');
      return false;
    }
    if (formData.emergencyPhone && !/^[6-9]\d{9}$/.test(formData.emergencyPhone)) {
      toast.error('Please enter a valid 10-digit emergency contact number.');
      return false;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions to proceed.');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.emailId)) {
      toast.error('Please enter a valid email address.');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      toast.error('Please enter a valid 10-digit mobile number.');
      return false;
    }
    if (formData.password !== formData.reenterPassword) {
      toast.error('Passwords do not match.');
      return false;
    }
    if (formData.password.length < 6 || formData.password.length > 20) {
      toast.error('Password must be between 6 and 20 characters.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateForm()) return;

    const [pincodeResult, mobileResult, emailResult] = await Promise.all([
      validatePincode(),
      validateContact('mobile'),
      validateContact('email'),
    ]);

    if (!pincodeResult.ok) {
      toast.error(pincodeResult.message || 'Please enter a valid pincode.');
      return;
    }
    if (!mobileResult.ok) {
      toast.error(mobileResult.message || 'Please enter a valid mobile number.');
      return;
    }
    if (!emailResult.ok) {
      toast.error(emailResult.message || 'Please enter a valid email ID.');
      return;
    }

    if (!locationName) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    const payload = {
      FullName: formData.fullName.trim(),
      Age: parseInt(formData.age, 10),
      Gender: formData.gender,
      MobileNumber: formData.mobileNumber,
      Email: formData.emailId.trim(),
      AddressLine: formData.address.trim(),
      Landmark: formData.landmark.trim() || null,
      DistrictID: idDistrict ?? null,
      PINCode: formData.pincode,
      EmergencyContactName: formData.emergencyName.trim() || 'N/A',
      EmergencyContactPhoneNumber: formData.emergencyPhone || 'N/A',
      KnownAllergies: formData.allergies.trim() || 'None',
      MedicalHistory: formData.medicalHistory.trim() || 'None',
      CurrentMedications: formData.currentMedications.trim() || 'None',
      IsEmailVerified: false,
      Password: formData.password,
    };

    try {
      setIsSubmitting(true);
      const response = await client.post('/api/patients', payload);
      const { success, message, data } = response.data || {};

      if (!success || !data) {
        toast.error(message || 'Registration failed. Please try again.');
        return;
      }

      toast.success(message || 'Registration successful!');
      navigate('/login', {
        state: {
          registeredEmail: data.email || formData.emailId,
          registeredName: data.fullName,
        },
      });
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(apiMessage || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    if (!window.confirm('Clear all fields? Any entered data will be lost.')) {
      return;
    }

    setFormData({ ...INITIAL_FORM_DATA });
    setShowPassword(false);
    setShowReenterPassword(false);
    setAcceptedTerms(false);
    setPincodeError('');
    setMobileError('');
    setEmailError('');
    setLocationName('');
    setIdDistrict(null);
    setIsValidatingPincode(false);
    setIsValidatingContact(false);
  };

  const renderSectionTitle = (num, title) => (
    <div className="reg-section-title">
      <span className="reg-section-num">{num}</span>
      <span>{title}</span>
    </div>
  );

  return (
    <div className="App">
      <Header />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-12">
              <div className="login-card">
                <div className="login-header text-center mb-4">
                  <h2 className="login-title">Patient Registration</h2>
                  <p className="login-subtitle">
                    Create your account to request home visits and telephone consultations from our verified doctors.
                  </p>
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  {/* Section 1: Personal Information */}
                  <div className="reg-form-section">
                    {renderSectionTitle(1, 'Personal Information')}
                    <div className="row">
                      <div className="col-12 p-2">
                        <label htmlFor="fullName" className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          maxLength="100"
                          required
                        />
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="age" className="form-label">
                          Age <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control"
                          id="age"
                          name="age"
                          value={formData.age}
                          onChange={handleChange}
                          placeholder="e.g. 35"
                          required
                        />
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="gender" className="form-label">
                          Gender <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Address Details */}
                  <div className="reg-form-section">
                    {renderSectionTitle(2, 'Address Details')}
                    <div className="row">
                      <div className="col-12 p-2">
                        <label htmlFor="address" className="form-label">
                          Address <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          id="address"
                          name="address"
                          rows="3"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="House / Flat No., Street, Locality..."
                          maxLength="200"
                          required
                        />
                        <small className="form-text text-muted d-block text-end">
                          {addressCount}/200 characters
                        </small>
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="landmark" className="form-label">
                          Landmark
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="landmark"
                          name="landmark"
                          value={formData.landmark}
                          onChange={handleChange}
                          placeholder="Nearby school, hospital, temple..."
                          maxLength="100"
                        />
                        <small className="form-text text-muted">
                          Optional – helps doctors locate you easily.
                        </small>
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="pincode" className="form-label">
                          PIN Code <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control w-25"
                            id="pincode"
                            name="pincode"
                            value={formData.pincode}
                            onChange={handleChange}
                            onBlur={validatePincode}
                            placeholder="e.g. 695001"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            required
                          />
                          <input
                            type="text"
                            className="form-control w-75 form-control-display-only"
                            value={locationName}
                            placeholder="District / Location"
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                          />
                        </div>
                        {pincodeError && (
                          <small className="form-text text-danger">{pincodeError}</small>
                        )}
                        {isValidatingPincode && (
                          <small className="form-text text-muted">Validating pincode...</small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Medical Details */}
                  <div className="reg-form-section">
                    {renderSectionTitle(3, 'Medical Details')}
                    <div className="row">
                      <div className="col-md-6 p-2">
                        <label htmlFor="emergencyName" className="form-label">
                          Emergency Contact Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="emergencyName"
                          name="emergencyName"
                          value={formData.emergencyName}
                          onChange={handleChange}
                          placeholder="Full name of emergency contact"
                          maxLength="100"
                        />
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="emergencyPhone" className="form-label">
                          Emergency Contact Phone
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="emergencyPhone"
                          name="emergencyPhone"
                          value={formData.emergencyPhone}
                          onChange={handleChange}
                          placeholder="Mobile number"
                          maxLength="10"
                        />
                      </div>

                      <div className="col-12 p-2">
                        <label htmlFor="allergies" className="form-label">
                          Known Allergies
                        </label>
                        <textarea
                          className="form-control"
                          id="allergies"
                          name="allergies"
                          rows="2"
                          value={formData.allergies}
                          onChange={handleChange}
                          placeholder="e.g. Penicillin, Aspirin, Pollen, Nuts... (write None if not applicable)"
                        />
                        <small className="form-text text-muted">
                          List any drug, food, or environmental allergies.
                        </small>
                      </div>

                      <div className="col-12 p-2">
                        <label htmlFor="medicalHistory" className="form-label">
                          Medical History
                        </label>
                        <textarea
                          className="form-control"
                          id="medicalHistory"
                          name="medicalHistory"
                          rows="3"
                          value={formData.medicalHistory}
                          onChange={handleChange}
                          placeholder="e.g. Diabetes Type 2 (since 2015), Hypertension... (write None if not applicable)"
                        />
                        <small className="form-text text-muted">
                          Include past illnesses, surgeries, hospitalizations, or chronic conditions.
                        </small>
                      </div>

                      <div className="col-12 p-2">
                        <label htmlFor="currentMedications" className="form-label">
                          Current Medications
                        </label>
                        <textarea
                          className="form-control"
                          id="currentMedications"
                          name="currentMedications"
                          rows="3"
                          value={formData.currentMedications}
                          onChange={handleChange}
                          placeholder="e.g. Metformin 500mg twice daily... (write None if not applicable)"
                        />
                        <small className="form-text text-muted">
                          List all medicines currently being taken including dosage if known.
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Contact */}
                  <div className="reg-form-section">
                    {renderSectionTitle(4, 'Contact Details')}
                    <div className="row">
                      <div className="col-md-6 p-2">
                        <label htmlFor="mobileNumber" className="form-label">
                          Mobile Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="mobileNumber"
                          name="mobileNumber"
                          value={formData.mobileNumber}
                          onChange={handleChange}
                          onBlur={() => validateContact('mobile')}
                          placeholder="9876543210"
                          maxLength="10"
                          pattern="[0-9]{10}"
                          required
                        />
                        {mobileError && (
                          <small className="form-text text-danger">{mobileError}</small>
                        )}
                      </div>

                      <div className="col-md-6 p-2">
                        <label htmlFor="emailId" className="form-label">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="emailId"
                          name="emailId"
                          value={formData.emailId}
                          onChange={handleChange}
                          onBlur={() => validateContact('email')}
                          placeholder="yourname@email.com"
                          maxLength="50"
                          required
                        />
                        {emailError && (
                          <small className="form-text text-danger">{emailError}</small>
                        )}
                        {isValidatingContact && (
                          <small className="form-text text-muted">Validating contact...</small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Account password */}
                  <div className="reg-form-section">
                    {renderSectionTitle(5, 'Account Password')}
                    <div className="row">
                      <div className="col-md-6 p-2">
                        <label htmlFor="password" className="form-label">
                          Password <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password (6-20 characters)"
                            minLength={6}
                            maxLength={20}
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPassword((v) => !v)}
                            tabIndex={-1}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-6 p-2">
                        <label htmlFor="reenterPassword" className="form-label">
                          Confirm Password <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type={showReenterPassword ? 'text' : 'password'}
                            className={`form-control ${
                              formData.reenterPassword && !passwordsMatch
                                ? 'is-invalid'
                                : formData.reenterPassword && passwordsMatch
                                  ? 'is-valid'
                                  : ''
                            }`}
                            id="reenterPassword"
                            name="reenterPassword"
                            value={formData.reenterPassword}
                            onChange={handleChange}
                            placeholder="Re-enter password"
                            minLength={6}
                            maxLength={20}
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowReenterPassword((v) => !v)}
                            tabIndex={-1}
                          >
                            {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        {formData.reenterPassword && !passwordsMatch && (
                          <small className="form-text text-danger">Passwords do not match</small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="reg-terms-row mt-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <label htmlFor="terms">
                      I agree to the Terms &amp; Conditions and Privacy Policy of 247 ProCare. I confirm that the information provided is accurate.
                    </label>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn login-submit-btn px-5 me-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Register Now'}
                    </button>
                    <button
                      type="button"
                      className="btn cancel-btn px-5"
                      onClick={handleClearForm}
                    >
                      Clear Form
                    </button>
                  </div>

                  <p className="text-center mt-3 mb-0 text-muted">
                    Already registered? <Link to="/login">Login here</Link>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PatientRegister;
